import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { HiCheckCircle } from "react-icons/hi";

import { CustomButton } from "../../atoms/CustomButton";
import { AlertMessage } from "../../atoms/AlertMessage";
import { Spinner } from "../../atoms/Spinner";
import {
  BookingClientProof,
  forgetRegistrationEnd,
  registrationEndOf,
  useFinishFormRegistration,
} from "../../../hooks/agenda/publicBooking";
import isSafeRedirectUrl from "../../../utils/safeRedirect";

const K = "bookingTunnel.form";
const AUTO_FINISH_SECONDS = 10;

type FinishState =
  | { kind: "open" }
  | { kind: "leaving" }
  | { kind: "done"; message: string };

const DONE_MESSAGES = {
  done: "registrationComplete.message",
  noPaymentLink: "customRegistration.page.toast.paymentLinkFailed",
  paymentUnavailable: "customRegistration.page.toast.paymentUnavailable",
};

/**
 * The end of the registration of a client a sign-up form sent here: the form's payment, its redirect or its message.
 * `formId` is null when the client did not come from a form, `proof` when nothing proves who they are.
 */
export const useFormRegistrationEnd = ({
  entityId,
  formId,
  userId,
  proof,
  onSessionExpired,
}: {
  entityId?: string;
  formId: string | null;
  userId?: string;
  proof: BookingClientProof | null;
  onSessionExpired?: () => void;
}) => {
  const { t } = useTranslation("clientView");
  const { mutate, isLoading } = useFinishFormRegistration(entityId);
  const [state, setState] = useState<FinishState>({ kind: "open" });
  const canFinish = Boolean(entityId && formId && userId && proof);
  const [cameBack, setCameBack] = useState(false);

  // Back from the page it left for through the history, the page returns as it was left.
  useEffect(() => {
    if (state.kind !== "leaving") return;
    const backFromLeaving = () => {
      setCameBack(true);
      setState({ kind: "open" });
    };
    window.addEventListener("pageshow", backFromLeaving);
    return () => window.removeEventListener("pageshow", backFromLeaving);
  }, [state.kind]);

  const finish = () => {
    if (!entityId || !formId || !userId || !proof) return;
    mutate(
      {
        formId,
        userId,
        proof,
        redirectUrl: registrationEndOf(entityId, formId),
      },
      {
        onSuccess: ({ action, url }) => {
          forgetRegistrationEnd(entityId, formId);
          if (action !== "message" && url && isSafeRedirectUrl(url)) {
            setState({ kind: "leaving" });
            window.location.assign(url);
          } else {
            setState({
              kind: "done",
              message:
                action === "payment"
                  ? DONE_MESSAGES.noPaymentLink
                  : DONE_MESSAGES.done,
            });
          }
        },
        onError: (error) => {
          const status = error?.response?.status;
          if (status === 400 || status === 404) {
            forgetRegistrationEnd(entityId, formId);
            setState({
              kind: "done",
              message:
                status === 400
                  ? DONE_MESSAGES.paymentUnavailable
                  : DONE_MESSAGES.done,
            });
          } else if (status === 401) {
            onSessionExpired?.();
          } else {
            toast.error(t(`${K}.finishError`) as string);
          }
        },
      }
    );
  };

  return { state, canFinish, finish, isFinishing: isLoading, cameBack };
};

/** After booking, the registration still to end; without booking, the way to end it anyway. */
export const FormFinishButton: React.FC<{
  booked: boolean;
  onClick: () => void;
  isLoading: boolean;
  /** Beside a payment still to make, which keeps the main button. */
  secondary?: boolean;
}> = ({ booked, onClick, isLoading, secondary = false }) => {
  const { t } = useTranslation("clientView");
  return (
    <CustomButton
      variant={booked && !secondary ? "primary" : "secondary"}
      title={t(booked ? `${K}.finish` : `${K}.finishWithout`) as string}
      className="w-full"
      onClick={onClick}
      isLoading={isLoading}
      disabled={isLoading}
    />
  );
};

/**
 * Under a booking's confirmation: the registration goes on by itself a moment later, or at once with the button.
 * Not `automatic` while the appointment is still to pay: the client pays first or ends with the button.
 */
export const FormFinishAfterBooking: React.FC<{
  onFinish: () => void;
  isFinishing: boolean;
  automatic?: boolean;
}> = ({ onFinish, isFinishing, automatic = true }) => {
  const { t } = useTranslation("clientView");
  const [left, setLeft] = useState<number | null>(
    automatic ? AUTO_FINISH_SECONDS : null
  );
  const finishRef = useRef(onFinish);
  finishRef.current = onFinish;

  // Filled after mounting, so that screen readers announce it, once and not at every second.
  const [announced, setAnnounced] = useState("");
  const countdown = t(`${K}.countdown`, {
    seconds: AUTO_FINISH_SECONDS,
  }) as string;
  useEffect(() => {
    setAnnounced(countdown);
  }, [countdown]);

  useEffect(() => {
    if (left === null) return;
    if (left === 0) {
      setLeft(null);
      finishRef.current();
      return;
    }
    const id = window.setTimeout(() => setLeft(left - 1), 1000);
    return () => window.clearTimeout(id);
  }, [left]);

  const finishNow = () => {
    setLeft(null);
    onFinish();
  };

  return (
    <div className="space-y-2">
      <FormFinishButton
        booked
        onClick={finishNow}
        isLoading={isFinishing}
        secondary={!automatic}
      />
      {left !== null && left > 0 && (
        <p className="text-center text-xs text-gray-500" aria-hidden="true">
          {t(`${K}.countdown`, { seconds: left })}
        </p>
      )}
      <p className="sr-only" role="status">
        {left !== null && announced}
      </p>
    </div>
  );
};

export const FormFinishLeaving: React.FC = () => {
  const { t } = useTranslation("clientView");
  return (
    <div
      className="flex items-center justify-center gap-2 py-8 text-sm text-gray-600"
      translate="no"
    >
      <Spinner size="sm" />
      {t(`${K}.leaving`)}
    </div>
  );
};

/** Under a booking's confirmation, or alone when the client ended without booking. */
export const FormFinishDone: React.FC<{ message: string; alone?: boolean }> = ({
  message,
  alone = false,
}) => {
  const { t } = useTranslation("clientView");
  if (!alone) {
    return <AlertMessage color="success">{t(message)}</AlertMessage>;
  }
  return (
    <div className="text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
        <HiCheckCircle size={28} aria-hidden="true" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900">
        {t(`${K}.doneTitle`)}
      </h2>
      <p className="mt-1 text-sm text-gray-600">{t(message)}</p>
    </div>
  );
};
